import type { CheckboxProps } from '@fluentui/react-components';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Label,
  MessageBar,
  Spinner,
  Tooltip,
} from '@fluentui/react-components';
import { EditRegular, PersonAddRegular } from '@fluentui/react-icons';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ChangePermissionsInput,
  FolderPermissionLevel,
  FolderPermissionReadAccess,
  GetFolderPermissionsEwsResult,
  GetUsersMsGraphResult,
  PermissionScope,
} from '../../../../api/types.gen';
import { PermissionsCreateGroup } from './components/permissionsDialogForm/groups/PermissionsCreateGroup';
import { PermissionsDeleteGroup } from './components/permissionsDialogForm/groups/PermissionsDeleteGroup';
import { PermissionsEditGroup } from './components/permissionsDialogForm/groups/PermissionsEditGroup';
import { PermissionsLevelSelect } from './components/permissionsDialogForm/groups/PermissionsLevelSelect';
import { PermissionsOtherGroup } from './components/permissionsDialogForm/groups/PermissionsOtherGroup';
import { PermissionsPersonPicker } from './components/permissionsDialogForm/groups/PermissionsPersonPicker';
import { PermissionsReadGroup } from './components/permissionsDialogForm/groups/PermissionsReadGroup';
import { getLevelNoneState } from './utilities/getFormStateFromPermissionLevel';

export interface IPermissionsDialogFormInitialInput extends ChangePermissionsInput {
  users: GetUsersMsGraphResult[];
}

export interface IPermissionsDialogForm extends IPermissionsDialogFormInitialInput {
  isEditMode: boolean;
}

interface IPermissionsDialogProps {
  initialPermissions?: IPermissionsDialogFormInitialInput;
  onSave: (permissionsToChange: ChangePermissionsInput, applyRecursive: boolean) => Promise<void>;
  isLoading?: boolean;
  dialogTitle: string;
  openingButtonLabel: string;
  folderInfo: GetFolderPermissionsEwsResult;
}

export const PermissionsDialogForm = ({
  initialPermissions,
  onSave,
  isLoading = false,
  dialogTitle,
  openingButtonLabel,
  folderInfo,
}: IPermissionsDialogProps) => {
  const form = useForm<IPermissionsDialogForm>({
    defaultValues: getDefaultValues(initialPermissions),
  });
  const { handleSubmit, reset, watch } = form;
  const { t } = useTranslation();
  // eslint-disable-next-line react-hooks/incompatible-library
  const users = watch('users');
  const renderOwnerPermissionWarning = form.watch('permissionLevel') === FolderPermissionLevel.OWNER;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [applyRecursive, setApplyRecursive] = useState<CheckboxProps['checked']>(false);
  return (
    <FormProvider {...form}>
      <Dialog
        modalType="modal"
        open={isDialogOpen}
        onOpenChange={(_, data) => {
          if (isLoading) {
            return;
          }
          setIsDialogOpen(data.open);
          if (!data.open) {
            reset();
            setApplyRecursive(false);
          }
        }}
      >
        <DialogTrigger disableButtonEnhancement>
          <Tooltip content={openingButtonLabel} relationship="label">
            {initialPermissions ? (
              <Button appearance="transparent" size="medium" disabled={isLoading} icon={<EditRegular />} />
            ) : (
              <Button appearance="primary" size="medium" disabled={isLoading} style={{ margin: '1rem 0 1rem 0' }} icon={<PersonAddRegular />}>
                {t('permissions.modifyAction.addTitle')}
              </Button>
            )}
          </Tooltip>
        </DialogTrigger>
        <DialogSurface>
          <form
            onSubmit={handleSubmit(async (d) => {
              const changeInputs = transformFormContentToChangePermissionsInput(d);
              await onSave(changeInputs, applyRecursive as boolean);
              reset();
              setApplyRecursive(false);
              setIsDialogOpen(false);
            })}
          >
            <DialogBody>
              <DialogTitle>{dialogTitle}</DialogTitle>
              <DialogContent style={{ display: 'grid', gap: 12 }}>
                {initialPermissions ? (
                  <Label>{initialPermissions?.users[0]?.displayName}</Label>
                ) : (
                  <PermissionsPersonPicker folderInfo={folderInfo} />
                )}
                <PermissionsLevelSelect />
                {renderOwnerPermissionWarning && (
                  <MessageBar intent="warning" layout="multiline" style={{ paddingRight: 'var(--spacingHorizontalM)' }}>
                    {t('permissions.modifyAction.levelGroup.warnings.owner')}
                  </MessageBar>
                )}
                <PermissionsCreateGroup />
                <PermissionsReadGroup />
                <PermissionsEditGroup />
                <PermissionsDeleteGroup />
                <PermissionsOtherGroup />
              </DialogContent>
              <DialogActions
                position="end"
                fluid
                style={{
                  marginTop: '1rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Checkbox
                  checked={applyRecursive}
                  onChange={(_, data) => setApplyRecursive(data.checked)}
                  label={t('permissions.modifyAction.applyRecursive')}
                  labelPosition="after"
                />
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {isLoading && <Spinner size="tiny" />}
                  <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary" disabled={isLoading}>
                      {t('permissions.modifyAction.closeButton')}
                    </Button>
                  </DialogTrigger>
                  <Button type="submit" appearance="primary" disabled={users.length === 0 || isLoading}>
                    {t('permissions.modifyAction.submitButton')}
                  </Button>
                </div>
              </DialogActions>
            </DialogBody>
          </form>
        </DialogSurface>
      </Dialog>
    </FormProvider>
  );
};

function getDefaultValues(input: IPermissionsDialogFormInitialInput | undefined): IPermissionsDialogForm {
  const isEditMode = Boolean(input);
  if (isEditMode) {
    return {
      users: input?.users ?? [],
      canCreateItems: input?.canCreateItems ?? false,
      canCreateSubfolders: input?.canCreateSubfolders ?? false,
      isFolderContact: input?.isFolderContact ?? false,
      isFolderOwner: input?.isFolderOwner ?? false,
      isFolderVisible: input?.isFolderVisible ?? false,
      deleteItems: input?.deleteItems ?? PermissionScope.NONE,
      editItems: input?.editItems ?? PermissionScope.NONE,
      readItems: input?.readItems ?? FolderPermissionReadAccess.NONE,
      permissionLevel: input?.permissionLevel ?? FolderPermissionLevel.CUSTOM,
      isEditMode,
    };
  }

  return {
    ...(getLevelNoneState() as IPermissionsDialogForm),
    users: [],
    permissionLevel: FolderPermissionLevel.NONE,
    isEditMode,
  };
}

function transformFormContentToChangePermissionsInput(formContent: IPermissionsDialogForm): ChangePermissionsInput {
  return {
    userMailCollection: formContent.users.map((user) => user.mail ?? ''),
    canCreateItems: formContent.canCreateItems,
    canCreateSubfolders: formContent.canCreateSubfolders,
    deleteItems: formContent.deleteItems,
    editItems: formContent.editItems,
    isFolderContact: formContent.isFolderContact,
    isFolderOwner: formContent.isFolderOwner,
    isFolderVisible: formContent.isFolderVisible,
    permissionLevel: formContent.permissionLevel,
    readItems: formContent.readItems,
  };
}
